import { useCallback, useEffect, useState } from "react";
import {
  getFtpStatus,
  getFtpHistory,
  getFtpFiles,
  getFtpChanges,
  triggerFtpSync,
  patchFtpFile,
  type FtpStatus,
  type FtpSyncRun,
  type FtpFile,
  type FtpChange,
} from "../../lib/ftpAdminApi.js";
import { Badge } from "../../ui/Badge.js";
import { Button } from "../../ui/Button.js";
import { LoadingBlock } from "../../ui/Spinner.js";
import { EmptyState } from "../../ui/EmptyState.js";
import { formatDate, formatRelative } from "../../lib/format.js";
import {
  SettingsIcon,
  DocumentIcon,
  AlertIcon,
  CheckIcon,
  CloseIcon,
  InfoIcon,
} from "../../lib/icons.js";

/**
 * /admin/ftp — administrace FTP synchronizace KTM.
 *
 * Sekce:
 *   1) Connection status card (host/user, TLS, password set?)
 *   2) Pending files banner + manual sync button
 *   3) Files table (sledované + pending review + error)
 *   4) History table (posledních 50 běhů)
 *   5) Changes feed (nové/změněné/smazané)
 */
export function FtpAdminPage(): JSX.Element {
  const [status, setStatus] = useState<FtpStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"files" | "history" | "changes">("files");
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const s = await getFtpStatus();
      setStatus(s);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Polling — aktualizuje status každých 10 s (během běžícího sync je důležité)
    const iv = setInterval(refresh, 10_000);
    return () => clearInterval(iv);
  }, [refresh]);

  async function handleSync(dry = false): Promise<void> {
    setSyncing(true);
    try {
      const r = await triggerFtpSync(dry);
      setToast(r.message);
      setTimeout(() => setToast(null), 4000);
      // Brzy refresh — sync bude běžet v pozadí
      setTimeout(refresh, 2000);
    } catch (err) {
      setError(String(err));
    } finally {
      setSyncing(false);
    }
  }

  if (loading) return <LoadingBlock label="Načítám FTP status…" />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">
            FTP synchronizace
          </h1>
          <p className="mt-1 text-zinc-500">
            Ceníky a replacementy z KTM dealer FTP. Sync běží každou noc,
            manuálně lze spustit tlačítkem níže.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() => handleSync(true)}
            disabled={syncing}
          >
            Jen kontrola (dry-run)
          </Button>
          <Button onClick={() => handleSync(false)} disabled={syncing}>
            {syncing ? "Spouštím…" : "Spustit sync"}
          </Button>
        </div>
      </div>

      {toast && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2">
          <CheckIcon size={16} />
          {toast}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {status && (
        <>
          <ConnectionCard status={status} />

          {status.pendingFilesCount > 0 && (
            <PendingBanner count={status.pendingFilesCount} onClick={() => setTab("files")} />
          )}

          <div className="mt-6 border-b border-zinc-200 flex items-center gap-1">
            <TabButton active={tab === "files"} onClick={() => setTab("files")}>
              Soubory
            </TabButton>
            <TabButton active={tab === "history"} onClick={() => setTab("history")}>
              Historie běhů
            </TabButton>
            <TabButton active={tab === "changes"} onClick={() => setTab("changes")}>
              Změny
            </TabButton>
          </div>

          <div className="mt-6">
            {tab === "files" && <FilesTab onChange={refresh} />}
            {tab === "history" && <HistoryTab />}
            {tab === "changes" && <ChangesTab />}
          </div>
        </>
      )}
    </div>
  );
}

/* ────────────────────── Connection ────────────────────── */

function ConnectionCard({ status }: { status: FtpStatus }): JSX.Element {
  const last = status.lastSync;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <SettingsIcon size={18} className="text-zinc-400" />
          <h3 className="font-semibold">Připojení</h3>
        </div>
        <dl className="space-y-2 text-sm">
          <Row label="Host" value={status.config.host} mono />
          <Row
            label="Uživatel"
            value={status.config.user ?? "nenastaveno"}
            mono
          />
          <Row
            label="Heslo"
            value={status.config.passwordSet ? "●●●●●●●● (nastaveno)" : "není"}
            tone={status.config.passwordSet ? "ok" : "warn"}
          />
          <Row
            label="TLS / SSL"
            value={status.config.secure ? "Explicit ✓" : "vypnuto"}
            tone={status.config.secure ? "ok" : "warn"}
          />
          <Row label="Lokální mirror" value={status.config.mirrorDir} mono />
        </dl>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 shadow-card p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DocumentIcon size={18} className="text-zinc-400" />
            <h3 className="font-semibold">Poslední sync</h3>
          </div>
          {last && (
            <Badge tone={syncStatusTone(last.status)}>{last.status}</Badge>
          )}
        </div>
        {!last ? (
          <div className="text-sm text-zinc-500">Zatím neproběhl žádný sync.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Metric label="Spuštěno" value={formatRelative(last.startedAt)} />
            <Metric
              label="Trvání"
              value={
                last.finishedAt
                  ? `${Math.max(
                      1,
                      Math.round(
                        (new Date(last.finishedAt).getTime() -
                          new Date(last.startedAt).getTime()) /
                          1000,
                      ),
                    )} s`
                  : "běží…"
              }
            />
            <Metric label="Ceny" value={last.pricesImported.toLocaleString("cs-CZ")} />
            <Metric
              label="Replacement"
              value={last.replacementsImported.toLocaleString("cs-CZ")}
            />
            <Metric label="Souborů objeveno" value={String(last.filesDiscovered)} />
            <Metric label="Nové" value={String(last.filesNew)} tone={last.filesNew > 0 ? "warn" : "neutral"} />
            <Metric label="Změněné" value={String(last.filesModified)} />
            <Metric label="Chyby" value={String(last.errors)} tone={last.errors > 0 ? "warn" : "ok"} />
          </div>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "ok" | "warn";
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-zinc-500">{label}</dt>
      <dd
        className={`${mono ? "font-mono text-[13px]" : ""} ${
          tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-zinc-900"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "neutral";
}): JSX.Element {
  const color =
    tone === "warn"
      ? "text-amber-600"
      : tone === "ok"
        ? "text-emerald-600"
        : "text-zinc-900";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</div>
      <div className={`text-base font-semibold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

/* ────────────────────── Pending banner ────────────────────── */

function PendingBanner({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-4 w-full flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-left hover:bg-amber-100 transition-colors"
    >
      <AlertIcon size={20} className="text-amber-600 flex-shrink-0" />
      <div className="flex-1">
        <div className="font-medium text-amber-900">
          {count} {count === 1 ? "nový soubor" : count < 5 ? "nové soubory" : "nových souborů"}
          {" "}čeká na kontrolu
        </div>
        <div className="text-sm text-amber-800/80">
          Zobrazit a rozhodnout, zda zpracovat nebo ignorovat
        </div>
      </div>
      <Badge tone="amber" className="text-sm">{count}</Badge>
    </button>
  );
}

/* ────────────────────── Tabs ────────────────────── */

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 -mb-px border-b-2 text-sm font-medium transition-colors ${
        active
          ? "border-ktm-orange text-ktm-orange"
          : "border-transparent text-zinc-500 hover:text-zinc-900"
      }`}
    >
      {children}
    </button>
  );
}

/* ────────────────────── Files ────────────────────── */

function FilesTab({ onChange }: { onChange: () => void }): JSX.Element {
  const [files, setFiles] = useState<FtpFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    setLoading(true);
    const params = filter === "all" ? {} : { status: filter };
    getFtpFiles(params)
      .then((r) => setFiles(r.items))
      .finally(() => setLoading(false));
  }, [filter]);

  async function setStatus(id: number, status: FtpFile["status"]): Promise<void> {
    await patchFtpFile(id, status);
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));
    onChange();
  }

  if (loading) return <LoadingBlock label="Načítám…" />;

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-1.5">
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>
          Vše ({files.length})
        </FilterPill>
        <FilterPill active={filter === "pending"} onClick={() => setFilter("pending")}>
          Čeká na review
        </FilterPill>
        <FilterPill
          active={filter === "processed"}
          onClick={() => setFilter("processed")}
        >
          Zpracováno
        </FilterPill>
        <FilterPill active={filter === "ignored"} onClick={() => setFilter("ignored")}>
          Ignorováno
        </FilterPill>
        <FilterPill active={filter === "error"} onClick={() => setFilter("error")}>
          Chyby
        </FilterPill>
      </div>

      {files.length === 0 ? (
        <EmptyState
          icon={<DocumentIcon size={24} />}
          title="Žádné soubory"
          description="Zkuste změnit filtr nebo spustit sync."
        />
      ) : (
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-card overflow-hidden">
          <ul className="divide-y divide-zinc-100">
            {files.map((f) => (
              <FileRow key={f.id} file={f} onStatus={setStatus} />
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 px-3 rounded-full text-sm font-medium transition-colors ${
        active
          ? "bg-ktm-black text-white"
          : "bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
      }`}
    >
      {children}
    </button>
  );
}

function FileRow({
  file,
  onStatus,
}: {
  file: FtpFile;
  onStatus: (id: number, status: FtpFile["status"]) => void;
}): JSX.Element {
  const clsTone = classificationTone(file.classification);
  return (
    <li className="p-5 flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-medium text-zinc-900">
                {file.filename}
              </span>
              <Badge tone={fileStatusTone(file.status)}>{file.status}</Badge>
              <Badge tone={clsTone}>
                {file.classification === "unknown"
                  ? "neznámý typ"
                  : file.classification === "price_list"
                    ? `ceník ${file.salesOrg ?? ""}`
                    : file.classification === "replacement"
                      ? `replacement ${file.salesOrg ?? ""}`
                      : `PIM ${file.brandCode ?? ""}`}
              </Badge>
            </div>
            <div className="mt-1 text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
              <span>{formatBytes(file.sizeBytes)}</span>
              <span>Změna na FTP: {formatRelative(file.modifiedAt)}</span>
              {file.lastProcessedAt && (
                <span>Zpracováno: {formatRelative(file.lastProcessedAt)}</span>
              )}
              {/* ⚠ Cesta na FTP nemá mezery — bez `break-all` přetéká kartu. */}
              <span className="font-mono text-zinc-400 break-all">{file.path}</span>
            </div>
            {file.notes && (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                {file.notes}
              </div>
            )}
          </div>

          {file.status === "pending" && (
            <div className="flex flex-col gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => onStatus(file.id, "monitored")}
                className="h-7 px-2.5 rounded-md text-xs font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
              >
                Pouze sledovat
              </button>
              <button
                type="button"
                onClick={() => onStatus(file.id, "ignored")}
                className="h-7 px-2.5 rounded-md text-xs font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
              >
                Ignorovat
              </button>
            </div>
          )}
          {file.status === "ignored" && (
            <button
              type="button"
              onClick={() => onStatus(file.id, "pending")}
              className="flex-shrink-0 h-7 px-2.5 rounded-md text-xs font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
            >
              Vrátit do review
            </button>
          )}
          {file.status === "error" && (
            <button
              type="button"
              onClick={() => onStatus(file.id, "pending")}
              className="flex-shrink-0 h-7 px-2.5 rounded-md text-xs font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
            >
              Zkusit znovu
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/* ────────────────────── History ────────────────────── */

function HistoryTab(): JSX.Element {
  const [items, setItems] = useState<FtpSyncRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFtpHistory(50)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingBlock />;
  if (items.length === 0)
    return (
      <EmptyState
        icon={<InfoIcon size={24} />}
        title="Zatím žádné běhy"
        description="Spusťte synchronizaci tlačítkem nahoře."
      />
    );

  return (
    // ⛔ `overflow-hidden` ROVNOU OŘEZÁVALO: tabulka má 8 sloupců, na mobilu se
    // vešly tři a sloupce Ceny/Replace/Chyby nešly ani odrolovat. Posuvník musí
    // být uvnitř, ne aby ho stránka jen skryla.
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-card overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-100">
            <th className="text-left px-5 py-3 font-medium">Spuštěno</th>
            <th className="text-left px-5 py-3 font-medium">Trig.</th>
            <th className="text-left px-5 py-3 font-medium">Stav</th>
            <th className="text-right px-5 py-3 font-medium">Trvání</th>
            <th className="text-right px-5 py-3 font-medium">Soubory</th>
            <th className="text-right px-5 py-3 font-medium">Ceny</th>
            <th className="text-right px-5 py-3 font-medium">Replace</th>
            <th className="text-right px-5 py-3 font-medium">Chyby</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {items.map((i) => (
            <tr key={i.runId} className="hover:bg-zinc-50">
              <td className="px-5 py-3 text-zinc-700" title={formatDate(i.startedAt)}>
                {formatRelative(i.startedAt)}
              </td>
              <td className="px-5 py-3">
                <Badge tone="neutral">{i.trigger}</Badge>
              </td>
              <td className="px-5 py-3">
                <Badge tone={syncStatusTone(i.status)}>{i.status}</Badge>
              </td>
              <td className="px-5 py-3 text-right tabular-nums text-zinc-600">
                {i.durationSec ?? "—"} s
              </td>
              <td className="px-5 py-3 text-right tabular-nums">
                {i.filesDiscovered}
                {i.filesNew > 0 && (
                  <span className="ml-1 text-amber-600">+{i.filesNew}</span>
                )}
              </td>
              <td className="px-5 py-3 text-right tabular-nums">
                {i.pricesImported.toLocaleString("cs-CZ")}
              </td>
              <td className="px-5 py-3 text-right tabular-nums">
                {i.replacementsImported.toLocaleString("cs-CZ")}
              </td>
              <td
                className={`px-5 py-3 text-right tabular-nums ${
                  i.errors > 0 ? "text-amber-700" : "text-zinc-400"
                }`}
              >
                {i.errors}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────── Changes ────────────────────── */

function ChangesTab(): JSX.Element {
  const [items, setItems] = useState<FtpChange[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFtpChanges(100)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingBlock />;
  if (items.length === 0)
    return (
      <EmptyState
        icon={<InfoIcon size={24} />}
        title="Žádné změny"
        description="Nové nebo změněné soubory se zobrazí tady po každém syncu."
      />
    );

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-card overflow-hidden">
      <ul className="divide-y divide-zinc-100">
        {items.map((c) => (
          <li key={c.id} className="px-5 py-3 flex items-start gap-3">
            <ChangeIcon type={c.changeType} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm text-zinc-900 truncate">
                  {c.path}
                </span>
                <Badge tone={changeTone(c.changeType)}>{c.changeType}</Badge>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {c.changeType === "modified" && c.oldSizeBytes != null && c.newSizeBytes != null && (
                  <>
                    {formatBytes(c.oldSizeBytes)} → {formatBytes(c.newSizeBytes)}{" "}
                    {c.newSizeBytes > c.oldSizeBytes
                      ? `(+${formatBytes(c.newSizeBytes - c.oldSizeBytes)})`
                      : `(−${formatBytes(c.oldSizeBytes - c.newSizeBytes)})`}
                    {"  ·  "}
                  </>
                )}
                {formatRelative(c.detectedAt)}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChangeIcon({ type }: { type: FtpChange["changeType"] }): JSX.Element {
  if (type === "new")
    return (
      <div className="w-7 h-7 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
        <CheckIcon size={14} />
      </div>
    );
  if (type === "modified")
    return (
      <div className="w-7 h-7 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
        <AlertIcon size={14} />
      </div>
    );
  return (
    <div className="w-7 h-7 rounded-md bg-red-50 text-red-700 flex items-center justify-center flex-shrink-0">
      <CloseIcon size={14} />
    </div>
  );
}

/* ────────────────────── helpers ────────────────────── */

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} kB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function syncStatusTone(s: string): "green" | "red" | "amber" | "neutral" {
  if (s === "success") return "green";
  if (s === "error") return "red";
  if (s === "partial") return "amber";
  return "neutral";
}

function fileStatusTone(s: string): "green" | "red" | "amber" | "neutral" {
  if (s === "processed") return "green";
  if (s === "error") return "red";
  if (s === "pending") return "amber";
  return "neutral";
}

function classificationTone(c: string): "orange" | "blue" | "neutral" {
  if (c === "price_list") return "orange";
  if (c === "replacement") return "blue";
  return "neutral";
}

function changeTone(c: string): "green" | "amber" | "red" {
  if (c === "new") return "green";
  if (c === "modified") return "amber";
  return "red";
}
